import { Flex, FlexItem, P, Popover } from "orcs-design-system";
import PropTypes from "prop-types";
import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import { useGroupTypes } from "src/contexts/global/WorkspaceContext";
import PersistedMoveIndicator from "./PersistedMoveIndicator";
import SourceOfTruthMissing from "./SourceOfTruthMissing";
import {
  getSourceType,
  isSourceTypeMissing,
  isTypeOnlyAdvancedMove,
} from "./persistedMoveSource";

const ACCESSIBLE_LABEL =
  "This team's type was changed in TeamForm; the type is saved and may differ from the source hierarchy.";

const PersistentTypeExplanation = () => (
  <P>
    This is a persistent type change, meaning this team type has been changed
    within TeamForm and may not match the source system hierarchy. This team
    will continue to use this type until it is manually changed again.
  </P>
);

const TypeMappingExplanation = () => (
  <P>
    A persistent type mapping is saved on this team. This team's own type has
    not changed.
  </P>
);

const PersistedMoveGroupTypeTooltipBody = ({
  sourceType,
  typeChanged,
  sourceTypeMissing,
  groupTypes,
}) => (
  <Flex flexDirection="column" gap="s">
    {typeChanged && sourceType && (
      <>
        <P mb="0">This team type was changed from:</P>
        <GroupTypeBadge group={{ type: sourceType }} groupTypes={groupTypes} />
      </>
    )}
    {!typeChanged && sourceType && (
      <>
        <P mb="0">Source type:</P>
        <GroupTypeBadge group={{ type: sourceType }} groupTypes={groupTypes} />
      </>
    )}
    {sourceTypeMissing && <SourceOfTruthMissing />}
    {typeChanged || sourceTypeMissing ? (
      <PersistentTypeExplanation />
    ) : (
      <TypeMappingExplanation />
    )}
  </Flex>
);

PersistedMoveGroupTypeTooltipBody.propTypes = {
  sourceType: PropTypes.string,
  typeChanged: PropTypes.bool,
  sourceTypeMissing: PropTypes.bool,
  groupTypes: PropTypes.object.isRequired,
};

const PersistedMoveGroupTypeInfo = ({ group, testId }) => {
  const groupTypes = useGroupTypes();

  const sourceType = getSourceType(group?.propertyOverrides?.groupType);
  const typeChanged = !!sourceType && group.type !== sourceType;
  const sourceTypeMissing = isSourceTypeMissing(group);
  const typeOnlyMove = isTypeOnlyAdvancedMove(group?.propertyOverrides);

  if (!typeChanged && !typeOnlyMove && !sourceTypeMissing) {
    return null;
  }

  const tooltip = (
    <PersistedMoveGroupTypeTooltipBody
      sourceType={sourceType}
      typeChanged={typeChanged}
      sourceTypeMissing={sourceTypeMissing}
      groupTypes={groupTypes}
    />
  );
  const ariaLabel =
    typeChanged || sourceTypeMissing
      ? ACCESSIBLE_LABEL
      : "A persistent type mapping is saved on this team. This team's own type has not changed.";

  return (
    <FlexItem
      flex="0 0 auto"
      alignSelf="center"
      role="button"
      tabIndex={0}
      aria-label="Persisted move information"
    >
      <Popover
        direction="bottom"
        width="320px"
        text={tooltip}
        ariaLabel={ariaLabel}
      >
        <PersistedMoveIndicator
          testId={testId}
          icon={["far", "square-a-lock"]}
        />
      </Popover>
    </FlexItem>
  );
};

PersistedMoveGroupTypeInfo.propTypes = {
  group: PropTypes.object.isRequired,
  testId: PropTypes.string,
};

export default PersistedMoveGroupTypeInfo;
